import { create } from "zustand";

interface FeedbackEntry {
  timestamp: string;
  url: string;
  componentName?: string;
  componentPath?: string;
  elementInfo?: string;
  comment: string;
}

interface FeedbackStore {
  entries: FeedbackEntry[];
  isOpen: boolean;
  lastClickInfo?: {
    componentName?: string;
    componentPath?: string;
    elementInfo: string;
    target: string;
  };
  toggleOpen: () => void;
  setLastClickInfo: (info: FeedbackStore["lastClickInfo"]) => void;
  submitFeedback: (comment: string) => void;
  clearEntries: () => void;
}

// Helper to find React component name from DOM element
function getComponentNameFromFiber(element: HTMLElement): string | undefined {
  // Try to get React Fiber node (works in dev mode)
  const fiberKey = Object.keys(element).find(
    (key) =>
      key.startsWith("__reactFiber") ||
      key.startsWith("__reactInternalInstance"),
  );

  if (fiberKey) {
    let fiber = (element as any)[fiberKey];

    // Walk up the fiber tree to find a named component
    while (fiber) {
      if (fiber.type && typeof fiber.type !== "string") {
        // Function/Class component
        const name =
          fiber.type.displayName || fiber.type.name || fiber.type._debugName;
        if (name && !name.startsWith("_")) {
          return name;
        }
      }
      fiber = fiber.return;
    }
  }

  return undefined;
}

// Helper to find the component file path from stack trace
function guessComponentPath(componentName?: string): string | undefined {
  if (!componentName) return undefined;

  // Try to match common patterns
  const patterns = [
    `src/components/${componentName}.tsx`,
    `src/pages/${componentName}.tsx`,
    `src/pages/${componentName.replace("Page", "")}.tsx`,
  ];

  return patterns[0]; // Return most likely path
}

// Helper to get detailed element info
function getElementInfo(element: HTMLElement): string {
  const parts: string[] = [];

  // Tag name
  parts.push(element.tagName.toLowerCase());

  // ID
  if (element.id) {
    parts.push(`#${element.id}`);
  }

  // Classes (limit to first 2)
  if (element.className && typeof element.className === "string") {
    const classes = element.className.split(" ").filter(Boolean).slice(0, 2);
    if (classes.length > 0) {
      parts.push(`.${classes.join(".")}`);
    }
  }

  // Text content preview (first 30 chars)
  const text = element.textContent?.trim().slice(0, 30);
  if (text) {
    parts.push(`"${text}${text.length >= 30 ? "..." : ""}"`);
  }

  return parts.join(" ");
}

// Helper to save feedback to local storage for file export
function saveFeedbackToFile(entry: FeedbackEntry) {
  try {
    const STORAGE_KEY = "ui_feedback_entries";
    const stored = localStorage.getItem(STORAGE_KEY);
    const entries: FeedbackEntry[] = stored ? JSON.parse(stored) : [];

    entries.push(entry);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));

    // Also trigger download if user wants it
    console.log(
      "💾 Feedback saved to localStorage. Use 'Export Feedback' button to download.",
    );
  } catch (error) {
    console.error("Failed to save feedback:", error);
  }
}

// Export function to download feedback as markdown
export function exportFeedbackToMarkdown(): string {
  const STORAGE_KEY = "ui_feedback_entries";
  const stored = localStorage.getItem(STORAGE_KEY);
  const entries: FeedbackEntry[] = stored ? JSON.parse(stored) : [];

  if (entries.length === 0) {
    return "# UI Feedback\n\nNo feedback entries yet.";
  }

  let markdown = "# UI Feedback Notes\n\n";
  markdown += `Generated: ${new Date().toISOString()}\n\n`;
  markdown += "---\n\n";

  entries.forEach((entry, idx) => {
    markdown += `## Entry ${idx + 1}\n\n`;
    markdown += `**Timestamp:** ${entry.timestamp}\n\n`;
    markdown += `**URL:** \`${entry.url}\`\n\n`;

    if (entry.componentName) {
      markdown += `**Component:** \`${entry.componentName}\`\n\n`;
    }

    if (entry.componentPath) {
      markdown += `**Path:** \`${entry.componentPath}\`\n\n`;
    }

    if (entry.elementInfo) {
      markdown += `**Element:** \`${entry.elementInfo}\`\n\n`;
    }

    markdown += `**Feedback:**\n\n${entry.comment}\n\n`;
    markdown += "---\n\n";
  });

  return markdown;
}

// Clear localStorage feedback
export function clearStoredFeedback() {
  localStorage.removeItem("ui_feedback_entries");
  console.log("🗑️ Cleared all stored feedback");
}

export const useFeedbackStore = create<FeedbackStore>((set, get) => ({
  entries: [],
  isOpen: false,
  lastClickInfo: undefined,

  toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),

  setLastClickInfo: (info) => set({ lastClickInfo: info }),

  submitFeedback: (comment: string) => {
    const clickInfo = get().lastClickInfo;
    const entry: FeedbackEntry = {
      timestamp: new Date().toISOString(),
      url: window.location.pathname,
      componentName: clickInfo?.componentName,
      componentPath: clickInfo?.componentPath,
      elementInfo: clickInfo?.elementInfo,
      comment,
    };

    console.log("🔔 UI FEEDBACK:", {
      timestamp: entry.timestamp,
      url: entry.url,
      component: entry.componentName || "Unknown",
      componentPath: entry.componentPath || "Not detected",
      element: entry.elementInfo,
      comment: entry.comment,
    });

    // Save to local file for batch processing
    saveFeedbackToFile(entry);

    set((state) => ({
      entries: [...state.entries, entry],
    }));
  },

  clearEntries: () => set({ entries: [] }),
}));

// Global click listener to auto-detect components
if (typeof window !== "undefined") {
  window.addEventListener(
    "click",
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // Skip if clicking on the feedback widget itself
      if (target.closest('[class*="feedback"]')) return;

      // Get component info
      const componentName = getComponentNameFromFiber(target);
      const componentPath = guessComponentPath(componentName);
      const elementInfo = getElementInfo(target);

      useFeedbackStore.getState().setLastClickInfo({
        componentName,
        componentPath,
        elementInfo,
        target: target.tagName.toLowerCase(),
      });
    },
    true, // Use capture phase to catch everything
  );
}
